import { useCallback, useState } from 'react';
import {
	useSensor,
	useSensors,
	DragStartEvent,
	DragEndEvent,
	DragOverEvent,
	PointerSensor,
} from '@dnd-kit/core';
import { getNumberedFieldName } from '../../components/general-components/input/gr-picture-input';
import { ContentTemplate } from '../../components/types/stylepacks';
import { Field } from '../../components/types/stylepacks';

export default function UseDragContent(
	dragEndCallback: Function,
	contentTemplate: ContentTemplate | null | undefined,
) {
	const [draggingCount, setDraggingCount] = useState(0);
	const [draggingOver, setDraggingOver] = useState(0);

	const sensors = useSensors(useSensor(PointerSensor));

	const handleDragStart = useCallback((event: DragStartEvent) => {
		const countMatches = event.active.id.match(/(\d+)/);
		if ((countMatches?.length ?? 0) > 0) {
			const count = countMatches ? Number(countMatches[0]) : 1;
			setDraggingCount(count);
		}
	}, []);

	const handleDragOver = useCallback((event: DragOverEvent) => {
		const positionMatches = event.over?.id.match(/(\d+)/);
		const countMatches = event.active.id.match(/(\d+)/);
		if ((countMatches?.length ?? 0) > 0 && (positionMatches?.length ?? 0) > 0) {
			const count = countMatches ? Number(countMatches[0]) : 1;
			const position = positionMatches ? Number(positionMatches[0]) : 1;

			const deltaY = event?.delta?.y ?? 0;

			const delta = (deltaY % 300) * (deltaY > 0 ? 1 : -1);
			const deltaDiff = count < position ? 200 : 100;

			if (draggingOver !== position && delta > deltaDiff) {
				setDraggingOver(position);
			}
		} else {
			setDraggingOver(0);
		}
	}, [draggingCount]);

	const handleDragEnd = useCallback((event: DragEndEvent) => {
		const position = draggingOver;
		const countMatches = event.active.id.match(/(\d+)/);

		setDraggingOver(0);
		setDraggingCount(0);

		if ((countMatches?.length ?? 0) > 0 && position > 0) {
			const count = countMatches ? Number(countMatches[0]) : 1;

			if (position !== count) {
				const originalValueName = event.active.id.replace(`${count}-`, '${i}-');
				const field = contentTemplate?.data?.variables.find(
					(subField: { userData: string }) => subField.userData === originalValueName,
				) as Field;

				if (field) {
					dragEndCallback(field, position, count);
				}
			}
		}
	}, [draggingCount, draggingOver]);

	return {
		draggingCount,
		draggingOver,
		handleDragEnd,
		handleDragOver,
		handleDragStart,
		sensors,
	};
}

export function reorderPictures(
	field: Field,
	values: Record<string, string>,
	position: number,
	count: number,
) {
	const pictureCount = Number(values.pictureCount ?? 1);
	const currentValues = { ...values };

	if (position === count) {
		return currentValues;
	}

	// move the rest of the data
	for (let i = 1; i <= pictureCount; i += 1) {
		if (i === position) {
			const newParentFieldName = getNumberedFieldName(field.userData, position);
			const newChildFieldNames: string[] = field.variables.map(
				(variable: any) => getNumberedFieldName(variable.userData, position),
			);
			const oldParentFieldName = getNumberedFieldName(field.userData, count);
			const oldChildFieldNames: string[] = field.variables.map(
				(variable: any) => getNumberedFieldName(variable.userData, count),
			);

			currentValues[newParentFieldName] = values[oldParentFieldName];
			newChildFieldNames.forEach((child: string, index: number) => {
				currentValues[child] = values[oldChildFieldNames[index]];
			});
		}
		if (i !== position) {
			// nothing has changed for these
			if ((i > position && i > count) || (i < position && i < count)) {
				continue;
			}

			const currentParent = getNumberedFieldName(field.userData, i);
			const currentChildren = field.variables.map(
				(variable: any) => getNumberedFieldName(variable.userData, i),
			);

			if (i > position) {
				const previousParent = getNumberedFieldName(field.userData, i - 1);
				const previousChildren = field.variables.map(
					(variable: any) => getNumberedFieldName(variable.userData, i - 1),
				);

				// replace current values
				currentValues[currentParent] = values[previousParent];
				currentChildren.forEach((child: string, index: number) => {
					currentValues[child] = values[previousChildren[index]];
				});
			} else if (i < position) {
				const previousParent = getNumberedFieldName(field.userData, i + 1);
				const previousChildren = field.variables.map(
					(variable: any) => getNumberedFieldName(variable.userData, i + 1),
				);

				// replace current values
				currentValues[currentParent] = values[previousParent];
				currentChildren.forEach((child: string, index: number) => {
					currentValues[child] = values[previousChildren[index]];
				});
			}
		}
	}

	return currentValues;
}
